import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import {
    getOffset,
    getPlacement,
    imageTravel,
    pictureRect,
    stepScale,
    zoomPlacement,
} from '@components/shared/spreadLayoutUtils';
import { SHEET_GRID, sheetGuides } from '@components/shared/sheetGrid';
import { movePicture, resizePicture, zoomPicture } from '@components/shared/sheetPlacement';

/** Pointer travel before a press on the page counts as a drag rather than a click. */
const DRAG_THRESHOLD = 4;

/** Wheel distance per scale step: one mouse-wheel notch, or a short trackpad swipe. */
const WHEEL_STEP = 50;

/** How long the grid and the cropped-off picture stay visible after the last zoom step. */
const ZOOM_SHOW_MS = 900;

const CORNER_CURSORS = { nw: 'nwse-resize', se: 'nwse-resize', ne: 'nesw-resize', sw: 'nesw-resize' };

/** The picture's own size: the given dimensions, or the <img> inside the page once loaded. */
function naturalOf(page, natural) {
    if (natural?.width && natural?.height) return natural;
    const img = page.querySelector('img');
    return img?.naturalWidth ? { width: img.naturalWidth, height: img.naturalHeight } : null;
}

function pageBox(page) {
    const rect = page.getBoundingClientRect();
    return { rect, size: { width: rect.width, height: rect.height } };
}

/** The page a picture is placed on: its window's parent, or the element with the handlers. */
const pageOf = (frame, el) => frame?.parentElement || el;

const samePlacement = (a, b) => a.x === b.x && a.y === b.y && (a.ox || 0) === (b.ox || 0) && (a.oy || 0) === (b.oy || 0);

const sameResult = (a, b) => a.scale === b.scale && samePlacement(a, b) && a.lines.x === b.lines.x && a.lines.y === b.lines.y;

/** What a placement saves: the position, and the move off the page when it carries one. */
const placementOf = ({ x, y, ox, oy }) => (ox === undefined && oy === undefined ? { x, y } : { x, y, ox, oy });

/** Pixels of a picture moved off its page that stay on the sheet, so it can be grabbed again. */
const KEEP_ON_SHEET = 40;

/** How far (dx / dy) the picture can go before it would leave the sheet. */
function sheetBounds(picture) {
    const sheet = picture.closest('.spread-container') || picture.closest('.isolate');
    if (!sheet) return null;
    const pic = picture.getBoundingClientRect();
    const own = sheet.getBoundingClientRect();
    const keepX = Math.min(KEEP_ON_SHEET, pic.width / 2);
    const keepY = Math.min(KEEP_ON_SHEET, pic.height / 2);
    return {
        minX: Math.min(0, own.left + keepX - pic.right),
        maxX: Math.max(0, own.right - keepX - pic.left),
        minY: Math.min(0, own.top + keepY - pic.bottom),
        maxY: Math.max(0, own.bottom - keepY - pic.top),
    };
}

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const percent = (value, total) => (value / total) * 100;

/**
 * Where the picture's page lies on its sheet and the picture's size there at
 * scale 100, in sheet percent (sheetPlacement), plus the sheet on screen. A
 * page of a pair (`free`) lies on the spread; any other page is its own sheet.
 */
function measureFrame(page, { free, natural, fill }) {
    const own = page.getBoundingClientRect();
    const fit = imageTravel(own, natural, 100, fill);
    if (!fit) return null;
    const spread = free ? page.closest('.spread-container') || page.closest('.isolate') : null;
    const sheet = spread ? spread.getBoundingClientRect() : own;
    const onSheet = {
        x: percent(own.left - sheet.left, sheet.width),
        y: percent(own.top - sheet.top, sheet.height),
        width: percent(own.width, sheet.width),
        height: percent(own.height, sheet.height),
    };
    return {
        page: onSheet,
        fit: { width: percent(fit.imageWidth, sheet.width), height: percent(fit.imageHeight, sheet.height) },
        guides: sheetGuides({ single: !spread, pageWidth: onSheet.width }),
        aspect: sheet.width / sheet.height,
        sheet: { left: sheet.left, top: sheet.top, width: sheet.width, height: sheet.height },
    };
}

/**
 * Place an image on its sheet's grid: drag it around, drag a corner to resize
 * it (a contained picture), or scroll over it to zoom around the mouse. Its
 * edges snap to the sheet's guides and grid (sheetGrid) unless Shift is held.
 * A press that does not move still reaches the page's click handler.
 *
 * With `free`, the picture can leave its page (ox / oy) for anywhere on the
 * spread, and the placement carries that move. Attach `frameRef` to the image's
 * window when the handlers sit on a smaller element (the picture itself); the
 * window's parent is the page that positions are measured against.
 *
 * @param {Object} options
 * @param {{ scale?: number, fill?: boolean, anchor?: string, x?: number, y?: number, ox?: number, oy?: number }} options.image
 * @param {{ width: number, height: number }} [options.natural] - picture size, when the page has no <img>
 * @param {(placement: { x: number, y: number, ox?: number, oy?: number }) => void} [options.onPlace] - omit to disable dragging
 * @param {(scale: number, placement: { x: number, y: number, ox?: number, oy?: number }) => void} [options.onScale] - omit to leave the wheel and corners alone
 * @param {boolean} [options.free] - let the image leave its page
 * @returns {{ placement, scale, guides, handles, handlers, frameRef }} live placement and scale, the grid while placing,
 *   corner handle props (PlacementHandles), pointer props, window ref
 */
export default function usePlacementDrag({ image, natural, onPlace, onScale, free = false }) {
    const [live, setLive] = useState(null);
    const [zoom, setZoom] = useState(null);
    const [hovering, setHovering] = useState(false);
    const stateRef = useRef(null);
    const suppressClickRef = useRef(false);
    const frameRef = useRef(null);

    const scale = image?.scale || 100;
    const fill = Boolean(image?.fill);
    const settled = getPlacement(image);
    const offset = free ? getOffset(image) : { ox: 0, oy: 0 };
    const wheelRef = useRef({ el: null, listener: null, distance: 0, pending: null, timer: null });
    const latestRef = useRef(null);
    latestRef.current = { scale, fill, placement: settled, offset, natural, onScale, free };

    useEffect(() => {
        wheelRef.current.pending = null;
    }, [scale, fill, settled.x, settled.y, offset.ox, offset.oy]);

    useEffect(() => () => clearTimeout(wheelRef.current.timer), []);

    // React registers wheel listeners as passive, so scrolling would also move the page.
    const pageRef = useCallback((el) => {
        const wheel = wheelRef.current;
        if (wheel.el && wheel.listener) wheel.el.removeEventListener('wheel', wheel.listener);
        wheel.el = el;
        if (!el) return;
        wheel.listener = (event) => {
            const latest = latestRef.current;
            if (!latest.onScale) return;
            event.preventDefault();
            const unit = event.deltaMode === 1 ? 16 : 1;
            // Some systems turn the wheel sideways while Shift is held.
            wheel.distance += (event.deltaY || (event.shiftKey ? event.deltaX : 0)) * unit;
            if (Math.abs(wheel.distance) < WHEEL_STEP) return;
            const steps = wheel.distance < 0 ? 1 : -1;
            wheel.distance = 0;
            const from = wheel.pending ?? { scale: latest.scale, ...latest.placement, ...latest.offset };
            const windowEl = frameRef.current || el;
            const pictureNatural = naturalOf(windowEl, latest.natural);
            const frame = measureFrame(pageOf(frameRef.current, el), { free: latest.free, natural: pictureNatural, fill: latest.fill });
            let next = null;
            if (frame && !latest.fill) {
                next = zoomPicture({
                    frame,
                    picture: { ...from, fill: false },
                    steps,
                    point: {
                        x: percent(event.clientX - frame.sheet.left, frame.sheet.width),
                        y: percent(event.clientY - frame.sheet.top, frame.sheet.height),
                    },
                    free: latest.free,
                    snap: !event.shiftKey,
                });
            } else {
                const toScale = stepScale(from.scale, latest.fill, steps);
                if (toScale !== from.scale) {
                    const { rect, size } = pageBox(windowEl);
                    const point = { x: event.clientX - rect.left, y: event.clientY - rect.top };
                    next = {
                        scale: toScale,
                        ...zoomPlacement({ page: size, natural: pictureNatural, fill: latest.fill, from, toScale, point }),
                        lines: null,
                    };
                }
            }
            if (!next) return;
            const { lines, ...picture } = next;
            wheel.pending = { ...from, ...picture };
            latest.onScale(picture.scale, placementOf(picture));
            setZoom({ frame, lines });
            clearTimeout(wheel.timer);
            wheel.timer = setTimeout(() => setZoom(null), ZOOM_SHOW_MS);
        };
        el.addEventListener('wheel', wheel.listener, { passive: false });
    }, []);

    const begin = useCallback((event, corner = null) => {
        suppressClickRef.current = false;
        if (event.button !== 0 || !(corner ? onScale : onPlace)) return;
        const hit = wheelRef.current.el || event.currentTarget;
        const frame = measureFrame(pageOf(frameRef.current, hit), {
            free,
            natural: naturalOf(frameRef.current || hit, natural),
            fill,
        });
        if (!frame) return;
        if (corner) {
            event.preventDefault();
            event.stopPropagation();
        }
        stateRef.current = {
            corner,
            startX: event.clientX,
            startY: event.clientY,
            frame,
            picture: { scale, fill, ...getPlacement(image), ...offset },
            bounds: free && !corner ? sheetBounds(hit) : null,
            free,
            moved: false,
            next: null,
        };
        try {
            hit.setPointerCapture(event.pointerId);
        } catch {
            // The pointer is already gone; moves still arrive while it is over the picture.
        }
    }, [image, natural, scale, fill, offset, onPlace, onScale, free]);

    const onPointerMove = useCallback((event) => {
        const state = stateRef.current;
        if (!state) return;
        let dx = event.clientX - state.startX;
        let dy = event.clientY - state.startY;
        if (!state.moved && Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
        if (state.bounds) {
            dx = clamp(dx, state.bounds.minX, state.bounds.maxX);
            dy = clamp(dy, state.bounds.minY, state.bounds.maxY);
        }
        state.moved = true;
        event.preventDefault();
        const { frame, corner } = state;
        const drag = {
            frame,
            picture: state.picture,
            dx: percent(dx, frame.sheet.width),
            dy: percent(dy, frame.sheet.height),
            free: state.free,
            snap: !event.shiftKey,
        };
        const next = corner ? resizePicture({ ...drag, corner }) : { scale: state.picture.scale, ...movePicture(drag) };
        if (state.next && sameResult(state.next, next)) return;
        state.next = next;
        setLive({ ...next, frame, corner });
    }, []);

    const finish = useCallback((event, commit) => {
        const state = stateRef.current;
        stateRef.current = null;
        const hit = wheelRef.current.el || event.currentTarget;
        if (hit.hasPointerCapture?.(event.pointerId)) hit.releasePointerCapture(event.pointerId);
        if (!state?.moved) return;
        suppressClickRef.current = true;
        setLive(null);
        const { next, picture } = state;
        if (!commit || !next) return;
        if (next.scale !== picture.scale) onScale?.(next.scale, placementOf(next));
        else if (!samePlacement(next, picture)) onPlace?.(placementOf(next));
    }, [onPlace, onScale]);

    const onClickCapture = useCallback((event) => {
        if (!suppressClickRef.current) return;
        suppressClickRef.current = false;
        event.preventDefault();
        event.stopPropagation();
    }, []);

    const shown = live || zoom;
    let placement = live ? { x: live.x, y: live.y } : settled;
    if (free) placement = { ...placement, ...(live ? { ox: live.ox, oy: live.oy } : offset) };
    let cursor = 'grab';
    if (live) cursor = live.corner ? CORNER_CURSORS[live.corner] : 'grabbing';

    return {
        placement,
        scale: live?.scale ?? scale,
        frameRef,
        guides: shown || hovering
            ? { frame: shown?.frame ?? null, lines: shown?.lines ?? null, placement, editing: Boolean(shown) }
            : null,
        handles: onPlace && onScale && !fill && (hovering || live)
            ? { onPointerDown: begin, active: live?.corner ?? null }
            : null,
        handlers: onPlace ? {
            ref: pageRef,
            onPointerEnter: (event) => event.pointerType === 'mouse' && setHovering(true),
            onPointerLeave: () => setHovering(false),
            onPointerDown: (event) => begin(event),
            onPointerMove,
            onPointerUp: (event) => finish(event, true),
            onPointerCancel: (event) => finish(event, false),
            onClickCapture,
            style: { touchAction: 'none', cursor },
        } : { ref: pageRef, style: undefined },
    };
}

const percentOf = (value, fallback = 50) => {
    const number = parseFloat(value);
    return String(value).trim().endsWith('%') && Number.isFinite(number) ? number : fallback;
};

const backgroundSizes = new Map();

/** A painted background's own size, once the browser has it (it is already decoded). */
function backgroundNatural(src) {
    let probe = backgroundSizes.get(src);
    if (!probe) {
        probe = new Image();
        probe.src = src;
        backgroundSizes.set(src, probe);
    }
    return probe.naturalWidth ? { width: probe.naturalWidth, height: probe.naturalHeight } : null;
}

/**
 * The page's sharpest painted picture, which the faded copy draws from: an
 * <img>, or the top background layer the slideshow paints with.
 */
function paintedPicture(page) {
    let best = null;
    page.querySelectorAll('img').forEach((img) => {
        if (img.complete && img.naturalWidth && (!best || img.naturalWidth > best.el.naturalWidth)) {
            const style = getComputedStyle(img);
            best = {
                el: img,
                src: img.currentSrc || img.src,
                natural: { width: img.naturalWidth, height: img.naturalHeight },
                fit: style.objectFit,
                position: style.objectPosition,
            };
        }
    });
    if (best) return best;
    const layers = [...page.querySelectorAll('div')].filter((el) => el.style.backgroundImage.startsWith('url('));
    const top = layers[layers.length - 1];
    if (!top) return null;
    const src = top.style.backgroundImage.slice(4, -1).replace(/^["']|["']$/g, '').replace(/\\"/g, '"');
    const natural = backgroundNatural(src);
    if (!natural) return null;
    const style = getComputedStyle(top);
    return { el: top, src, natural, fit: style.backgroundSize, position: style.backgroundPosition };
}

/**
 * While a picture is placed, everything outside its sheet of paper (the
 * slideshow's spread) is greyed out, and the whole picture shows faded beyond
 * what shows it, so the parts that are cropped off stay visible. Both live in
 * <body> because the page and the sheet clip their content; where the real
 * picture shows (its window when that crops it, else the sheet) is cut out of
 * the faded one.
 */
function PlacementOverflow({ editing }) {
    const anchorRef = useRef(null);
    const ghostRef = useRef(null);
    const veilRef = useRef(null);
    const editingRef = useRef(editing);
    editingRef.current = editing;
    const [doc, setDoc] = useState(null);

    useLayoutEffect(() => {
        setDoc(anchorRef.current?.ownerDocument || null);
    }, []);

    useLayoutEffect(() => {
        const page = anchorRef.current?.parentElement?.parentElement;
        const ghost = ghostRef.current;
        const veil = veilRef.current;
        if (!page || !ghost || !veil) return undefined;
        const sheet = page.closest('.spread-container');
        let frame = 0;
        const draw = () => {
            frame = requestAnimationFrame(draw);
            const rect = sheet?.getBoundingClientRect();
            if (rect) {
                veil.style.left = `${rect.left}px`;
                veil.style.top = `${rect.top}px`;
                veil.style.width = `${rect.width}px`;
                veil.style.height = `${rect.height}px`;
                veil.style.opacity = '1';
            }
            const picture = editingRef.current ? paintedPicture(page) : null;
            if (!picture) {
                ghost.style.display = 'none';
                return;
            }
            const [posX = '50%', posY = '50%'] = picture.position.split(/\s+/);
            const pic = pictureRect(
                picture.el.getBoundingClientRect(),
                picture.natural,
                picture.fit === 'cover' ? 'cover' : 'contain',
                { x: percentOf(posX), y: percentOf(posY) },
            );
            const own = page.getBoundingClientRect();
            const view = !rect || getComputedStyle(page).overflow !== 'visible' ? own : rect;
            const left = Math.max(view.left, rect?.left ?? view.left);
            const top = Math.max(view.top, rect?.top ?? view.top);
            const right = Math.max(left, Math.min(view.right, rect?.right ?? view.right));
            const bottom = Math.max(top, Math.min(view.bottom, rect?.bottom ?? view.bottom));
            const x1 = left - pic.left;
            const y1 = top - pic.top;
            const x2 = right - pic.left;
            const y2 = bottom - pic.top;
            const { src } = picture;
            ghost.style.display = 'block';
            ghost.style.left = `${pic.left}px`;
            ghost.style.top = `${pic.top}px`;
            ghost.style.width = `${pic.width}px`;
            ghost.style.height = `${pic.height}px`;
            if (ghost.dataset.src !== src) {
                ghost.dataset.src = src;
                ghost.style.backgroundImage = `url("${src.replace(/"/g, '\\"')}")`;
            }
            ghost.style.clipPath = `path(evenodd, "M0 0H${pic.width}V${pic.height}H0Z M${x1} ${y1}H${x2}V${y2}H${x1}Z")`;
        };
        draw();
        return () => cancelAnimationFrame(frame);
    }, [doc]);

    return (
        <>
            <span ref={anchorRef} hidden />
            {doc && createPortal(
                <>
                    <div
                        ref={veilRef}
                        aria-hidden="true"
                        className="pointer-events-none fixed z-[2147483000] opacity-0 transition-opacity duration-150"
                        style={{ boxShadow: '0 0 0 200vmax rgb(115 115 115 / 0.55)' }}
                    />
                    <div
                        ref={ghostRef}
                        aria-hidden="true"
                        className="pointer-events-none fixed z-[2147483000] bg-no-repeat opacity-40 outline-dashed outline-1 -outline-offset-1 outline-purple-500"
                        style={{ display: 'none', backgroundSize: '100% 100%' }}
                    />
                </>,
                doc.body,
            )}
        </>
    );
}

/**
 * While the image is placed: the sheet's grid (unless a PlacementGrid is drawn
 * on the page instead), and the faded picture beyond what shows it during a
 * drag or zoom. Render it in the image's window.
 */
export function PlacementGuides({ guides, grid = true }) {
    if (!guides) return null;
    return (
        <div className="pointer-events-none absolute inset-0 z-10" aria-hidden="true">
            <PlacementOverflow editing={Boolean(guides.editing)} />
            {grid && <PlacementGrid guides={guides} />}
        </div>
    );
}

/** The grid's lines across the sheet, in sheet percent. */
const SHEET_STEPS = Array.from({ length: Math.round(100 / SHEET_GRID) + 1 }, (_, index) => index * SHEET_GRID);

/**
 * The sheet's snap grid while a picture is placed: a dot on every grid step
 * across the whole sheet, faint lines on its guides, and the guide the picture
 * meets. Render it in the picture's page; for a picture that can leave its page
 * (`free`) on the page rather than in the moving window, above both pages.
 */
export function PlacementGrid({ guides, free = false }) {
    const frame = guides?.frame;
    if (!frame) return null;
    const { page } = frame;
    const lines = guides.lines || {};
    const sheet = {
        left: `${(-page.x / page.width) * 100}%`,
        top: `${(-page.y / page.height) * 100}%`,
        width: `${(100 / page.width) * 100}%`,
        height: `${(100 / page.height) * 100}%`,
    };
    return (
        <div className={`pointer-events-none absolute ${free ? 'z-30' : ''}`} style={sheet} aria-hidden="true" data-placement-grid>
            {frame.guides.x.map((x) => (
                <span key={`x${x}`} className="absolute inset-y-0 w-px -translate-x-1/2 bg-purple-500/25" style={{ left: `${x}%` }} />
            ))}
            {frame.guides.y.map((y) => (
                <span key={`y${y}`} className="absolute inset-x-0 h-px -translate-y-1/2 bg-purple-500/25" style={{ top: `${y}%` }} />
            ))}
            {SHEET_STEPS.map((y) => SHEET_STEPS.map((x) => (
                <span
                    key={`${x}-${y}`}
                    className="absolute h-1 w-1 -translate-x-1/2 -translate-y-1/2 rounded-full bg-purple-500/60"
                    style={{ left: `${x}%`, top: `${y}%` }}
                />
            )))}
            {lines.x != null && <span className="absolute inset-y-0 w-px -translate-x-1/2 bg-purple-600" style={{ left: `${lines.x}%` }} />}
            {lines.y != null && <span className="absolute inset-x-0 h-px -translate-y-1/2 bg-purple-600" style={{ top: `${lines.y}%` }} />}
        </div>
    );
}

const CORNERS = ['nw', 'ne', 'sw', 'se'];

/**
 * Corner handles that resize a contained picture on the grid. Render them in
 * the picture's pointer layer (the hook's handlers), which they follow.
 */
export function PlacementHandles({ handles }) {
    if (!handles) return null;
    return CORNERS.map((corner) => (
        <span
            key={corner}
            aria-hidden="true"
            data-placement-handle={corner}
            className={`absolute h-2.5 w-2.5 rounded-sm border border-purple-600 shadow-sm ${handles.active === corner ? 'bg-purple-600' : 'bg-white'}`}
            style={{
                [corner.includes('n') ? 'top' : 'bottom']: 0,
                [corner.includes('w') ? 'left' : 'right']: 0,
                transform: `translate(${corner.includes('w') ? '-50%' : '50%'}, ${corner.includes('n') ? '-50%' : '50%'})`,
                cursor: CORNER_CURSORS[corner],
            }}
            onPointerDown={(event) => handles.onPointerDown(event, corner)}
        />
    ));
}

PlacementOverflow.propTypes = {
    editing: PropTypes.bool,
};

const guidesShape = PropTypes.shape({
    frame: PropTypes.object,
    lines: PropTypes.object,
    placement: PropTypes.object.isRequired,
    editing: PropTypes.bool,
});

PlacementGuides.propTypes = {
    guides: guidesShape,
    /** False when a PlacementGrid is drawn on the page instead. */
    grid: PropTypes.bool,
};

PlacementGrid.propTypes = {
    guides: guidesShape,
    free: PropTypes.bool,
};

PlacementHandles.propTypes = {
    handles: PropTypes.shape({
        onPointerDown: PropTypes.func.isRequired,
        active: PropTypes.string,
    }),
};
